import { Person } from '@mui/icons-material';
import LinkIcon from '@mui/icons-material/Link';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  TextField,
  useTheme,
} from '@mui/material';
import { useRequest } from 'ahooks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { walletEvents } from '../../../events/walletEvents';
import WalletService from '../../../services/walletService';
import { showErrorToast, showSuccessToast } from '../../../utils/toast';

interface InlineWalletCreatorProps {
  onCancel: () => void;
  onSuccess: (wallet: any) => void;
}

const InlineWalletCreator = ({ onCancel, onSuccess }: InlineWalletCreatorProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'wallets', 'loan']);
  const [walletName, setWalletName] = useState('');

  const { loading, run } = useRequest(WalletService.createLoanWallet, {
    manual: true,
    onSuccess: (result: any) => {
      // Handle both response structures: result.id or result.data.id
      const walletId = result?.data?.id || result?.id;
      const walletBalance = result?.data?.balance ?? result?.balance ?? 0;

      walletEvents.publish('wallet:created', {
        type: 'loan',
        walletId: walletId,
      });

      showSuccessToast(
        t('loanWallet.create_success', { ns: 'wallets' }),
        { hideProgressBar: true }
      );

      // Pass the created wallet data to parent to auto-select
      onSuccess({
        id: 'loan',
        title: t('loan_wallets', { ns: 'wallets', defaultValue: 'Ví vay nợ' }),
        wallet: {
          id: walletId,
          name: walletName.trim(), // Use the entered name directly
          balance: walletBalance,
          type: 'LOAN',
        },
      });
    },
    onError: (error) => {
      showErrorToast(
        error.message || t('loanWallet.create_error', { ns: 'wallets' }),
        { hideProgressBar: true }
      );
    },
  });

  const handleSubmit = () => {
    if (!walletName.trim()) return;

    run({
      name: walletName.trim(),
      isInteractionEnabled: false,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && walletName.trim()) {
      handleSubmit();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        width: '100%',
        marginBottom: theme.spacing(1),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          py: 1,
          px: 2,
          width: '100%',
        }}
      >
        {/* Avatar with overlay icon */}
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: '50%',
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme.palette.teal[500],
              color: theme.palette.common.white,
            }}
          >
            <Person sx={{ fontSize: 20 }} />
          </Card>
          {/* Small overlay icon */}
          <Box
            sx={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              width: 16,
              height: 16,
              borderRadius: '50%',
              bgcolor: theme.palette.grey[100],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${theme.palette.common.white}`,
            }}
          >
            <LinkIcon sx={{ fontSize: 10, color: theme.palette.grey[600] }} />
          </Box>
        </Box>

        {/* Input field */}
        <TextField
          autoFocus
          size="small"
          fullWidth
          placeholder={t('transaction_form.enter_name', { ns: 'loan', defaultValue: 'Nhập tên' })}
          value={walletName}
          onChange={(e) => setWalletName(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              '& fieldset': {
                borderColor: 'transparent',
              },
              '&:hover fieldset': {
                borderColor: 'transparent',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'transparent',
              },
            },
            '& .MuiInputBase-input': {
              py: 0.75,
            },
          }}
        />

        {/* Action buttons */}
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !walletName.trim()}
            sx={{
              minWidth: 'auto',
              px: 2,
              py: 0.5,
              bgcolor: theme.palette.teal[500],
              '&:hover': {
                bgcolor: theme.palette.teal[600],
              },
            }}
          >
            {loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              t('save', { ns: 'common', defaultValue: 'Lưu' })
            )}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
            sx={{
              minWidth: 'auto',
              px: 2,
              py: 0.5,
              borderColor: theme.palette.grey[300],
              color: theme.palette.text.primary,
              '&:hover': {
                borderColor: theme.palette.grey[400],
                bgcolor: theme.palette.grey[50],
              },
            }}
          >
            {t('cancel', { ns: 'common', defaultValue: 'Hủy' })}
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
};

export default InlineWalletCreator;
